import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { blockService, departmentService, programmeService } from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import "./MasterScreen.css";

const BlockPage = () => {
  const { user } = useContext(AuthContext);
  const [blocks, setBlocks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", department: "", programme: "" });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (user?.role === "SuperAdmin") fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [blockRes, deptRes, progRes] = await Promise.all([
        blockService.getAll(),
        departmentService.getAll(),
        programmeService.getAll(),
      ]);
      setBlocks(blockRes.data || []);
      setDepartments(deptRes.data || []);
      setProgrammes(progRes.data || []);
      setError("");
    } catch (err) {
      setError("Infrastructure data synchronization failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await blockService.update(editingId, formData);
      } else {
        await blockService.create(formData);
      }
      setFormData({ name: "", department: "", programme: "" });
      setEditingId(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      setError("Operation failed: Database write error.");
    }
  };

  const handleEdit = (block) => {
    setFormData({
      ...block,
      department: block.department?._id || block.department,
      programme: block.programme?._id || block.programme,
    });
    setEditingId(block._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await blockService.delete(deleteTargetId);
      setDeleteTargetId(null);
      fetchData();
    } catch (err) {
      setError("Deletion failed: Structural dependency detected.");
      setDeleteTargetId(null);
    }
  };

  const filteredBlocks = blocks.filter(
    (block) =>
      block.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.department?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.programme?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="loading">Mapping Structural Blocks...</div>;

  return (
    <div className="master-screen animated-bg">
      <div className="screen-content">
        <header className="screen-header-rail">
          <h1>Block Infrastructure</h1>
          <button
            onClick={() => { setShowForm(!showForm); if (showForm) setEditingId(null); }}
            className="btn-primary"
          >
            {showForm ? "✕ Close Interface" : "＋ Register Block"}
          </button>
        </header>

        {error && <div className="error-message">{error}</div>}

        {showForm && (
          <form className="master-form-glass" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Designation Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Science Block A"
                  required
                />
              </div>
              <div className="form-group">
                <label>Target Department</label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Associated Programme</label>
                <select
                  value={formData.programme}
                  onChange={(e) => setFormData({ ...formData, programme: e.target.value })}
                  required
                >
                  <option value="">Select Academic Link</option>
                  {programmes.map((prog) => (
                    <option key={prog._id} value={prog._id}>{prog.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
              {editingId ? "Update Designation" : "Establish Block"}
            </button>
          </form>
        )}

        <section className="master-table-card">
          <div className="table-action-header" style={{ flexWrap: 'wrap', gap: '1rem' }}>
            <h3>Infrastructure Registry ({filteredBlocks.length})</h3>
            <input
              type="text"
              placeholder="🔍 Search block name, department, or programme..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '300px' }}
            />
          </div>
          <div className="table-wrapper">
            <table className="standard-table">
              <thead>
                <tr>
                  <th>Block Identity</th>
                  <th>Department Link</th>
                  <th>Core Programme</th>
                  <th style={{ textAlign: 'right' }}>Management</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlocks.map((block) => (
                  <tr key={block._id}>
                    <td style={{ fontWeight: 700 }}>{block.name}</td>
                    <td>{block.department?.name || "N/A"}</td>
                    <td><span className="status-tag tag-progress">{block.programme?.name || "N/A"}</span></td>
                    <td>
                      <div className="action-btn-group">
                        <button className="btn-icon-only" onClick={() => handleEdit(block)} title="Edit">✎</button>
                        <button className="btn-icon-only btn-delete-icon" onClick={() => setDeleteTargetId(block._id)} title="Delete">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredBlocks.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '3rem', opacity: 0.6 }}>
                      No block records matching query.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ConfirmModal
          isOpen={!!deleteTargetId}
          title="Delete Block?"
          message="Are you sure you want to permanently remove this infrastructure block?"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTargetId(null)}
        />
      </div>
    </div>
  );
};

export default BlockPage;
